import React, { useEffect, useMemo, useState } from 'react';
import { AlertCircle, FileText, Image as ImageIcon, Loader2 } from 'lucide-react';
import Papa from 'papaparse';
import { CSVPreview } from './CSVPreview';

interface FilePreviewProps {
  files: File[];
  compact?: boolean;
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function ImagePreview({ file, compact }: { file: File; compact?: boolean }) {
  const [url, setUrl] = useState<string>();
  useEffect(() => {
    const objectUrl = URL.createObjectURL(file);
    setUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [file]);

  if (!url) return <div className="h-20 animate-pulse rounded-lg bg-slate-100" />;
  return <img src={url} alt={file.name} className={`w-full rounded-lg object-contain ${compact ? 'max-h-48' : 'max-h-80'}`} />;
}

function CsvFilePreview({ file }: { file: File }) {
  const [showPreview, setShowPreview] = useState(true);
  const [rows, setRows] = useState<Record<string, unknown>[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>();

  useEffect(() => {
    setShowPreview(true);
  }, [file]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(undefined);
    Papa.parse<Record<string, unknown>>(file, {
      header: true,
      skipEmptyLines: true,
      preview: 100,
      worker: true,
      complete: (result) => {
        if (!active) return;
        if (result.errors?.length) setError(result.errors[0]?.message || 'Unable to parse CSV.');
        setRows((result.data || []).map((row, index) => ({ rowNumber: index + 1, ...row })));
        setLoading(false);
      },
      error: (reason) => {
        if (!active) return;
        setError(reason?.message || 'Unable to read CSV.');
        setLoading(false);
      },
    });
    return () => { active = false; };
  }, [file]);

  if (loading) {
    return <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-3 text-[11px] text-slate-500"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Reading first 100 CSV rows…</div>;
  }
  if (error && rows.length === 0) {
    return <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2.5 text-[11px] text-red-700"><AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />{error}</div>;
  }
  return <div className="space-y-1.5">{error && <div className="text-[10px] text-amber-700">Preview parser warning: {error}</div>}<CSVPreview csvPreviewData={rows} showPreview={showPreview} onTogglePreview={() => setShowPreview((current) => !current)} /></div>;
}

export default function FilePreview({ files, compact = false }: FilePreviewProps) {
  const imageFiles = useMemo(() => files.filter((file) => file.type.startsWith('image/')), [files]);
  const csvFiles = useMemo(() => files.filter((file) => file.type === 'text/csv' || /\.csv$/i.test(file.name)), [files]);
  const otherFiles = useMemo(() => files.filter((file) => !imageFiles.includes(file) && !csvFiles.includes(file)), [files, imageFiles, csvFiles]);

  if (!files.length) return null;

  return (
    <div className="space-y-2.5 rounded-xl border border-slate-200 bg-slate-50/60 p-3">
      <div className="flex items-center justify-between gap-2">
        <div>
          <div className="text-[11px] font-semibold text-slate-700">File preview</div>
          <div className="text-[10px] text-slate-400">{files.length} file{files.length === 1 ? '' : 's'} · previews stay in the browser</div>
        </div>
      </div>

      {imageFiles.map((file) => (
        <div key={`${file.name}-${file.lastModified}`} className="overflow-hidden rounded-lg border border-slate-200 bg-white p-2.5">
          <div className="mb-2 flex items-center gap-2 text-[10px] text-slate-500"><ImageIcon className="h-3.5 w-3.5" /><span className="min-w-0 flex-1 truncate">{file.name}</span><span>{formatBytes(file.size)}</span></div>
          <ImagePreview file={file} compact={compact} />
        </div>
      ))}

      {csvFiles.map((file) => (
        <div key={`${file.name}-${file.lastModified}`} className="overflow-hidden rounded-lg border border-slate-200 bg-white p-2.5">
          <div className="mb-2 flex items-center gap-2 text-[10px] text-slate-500"><FileText className="h-3.5 w-3.5" /><span className="min-w-0 flex-1 truncate">{file.name}</span><span>{formatBytes(file.size)}</span></div>
          <CsvFilePreview file={file} />
        </div>
      ))}

      {otherFiles.map((file) => (
        <div key={`${file.name}-${file.lastModified}`} className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-[10px] text-slate-500">
          <FileText className="h-3.5 w-3.5 shrink-0" /><span className="min-w-0 flex-1 truncate">{file.name}</span><span>{formatBytes(file.size)}</span>
        </div>
      ))}
    </div>
  );
}
